import type { CampaignAnalyticsGranularity, PlacementMetrics } from "@adpilot/shared";
import type {
  PerformanceKpi,
  PerformanceMetric,
  PerformanceTrendPoint,
} from "@/components/shared/performance/performance";

type Volume = Pick<PlacementMetrics, "impressions" | "clicks" | "spend" | "sales" | "orders">;

/** Same definitions the campaign-analytics read model uses, so cards read alike. */
const KPI_DEFS: ReadonlyArray<Pick<PerformanceKpi, "key" | "label" | "semantics" | "format">> = [
  { key: "spend", label: "Spend", semantics: "positive-is-bad", format: "currency" },
  { key: "sales", label: "Ad Sales", semantics: "positive-is-good", format: "currency" },
  { key: "orders", label: "Orders", semantics: "positive-is-good", format: "number" },
  { key: "acos", label: "ACOS", semantics: "positive-is-bad", format: "percent" },
  { key: "cpc", label: "CPC", semantics: "positive-is-bad", format: "currency" },
  { key: "cpa", label: "CPA", semantics: "positive-is-bad", format: "currency" },
  { key: "roas", label: "ROAS", semantics: "positive-is-good", format: "number" },
  { key: "clicks", label: "Clicks", semantics: "neutral", format: "number" },
  { key: "impressions", label: "Impressions", semantics: "neutral", format: "number" },
  { key: "ctr", label: "CTR", semantics: "positive-is-good", format: "percent" },
  { key: "cvr", label: "CVR", semantics: "positive-is-good", format: "percent" },
];

const EMPTY: Volume = { impressions: 0, clicks: 0, spend: 0, sales: 0, orders: 0 };

function sum(rows: readonly Volume[]): Volume {
  return rows.reduce<Volume>(
    (total, row) => ({
      impressions: total.impressions + row.impressions,
      clicks: total.clicks + row.clicks,
      spend: total.spend + row.spend,
      sales: total.sales + row.sales,
      orders: total.orders + row.orders,
    }),
    EMPTY,
  );
}

/** Volume plus ratios; a ratio is null when its denominator is zero. */
export function withRatios(volume: Volume): PlacementMetrics {
  const { impressions, clicks, spend, sales, orders } = volume;
  return {
    ...volume,
    spend: Math.round(spend * 100) / 100,
    sales: Math.round(sales * 100) / 100,
    ctr: impressions > 0 ? clicks / impressions : null,
    cpc: clicks > 0 ? spend / clicks : null,
    cvr: clicks > 0 ? orders / clicks : null,
    cpa: orders > 0 ? spend / orders : null,
    acos: sales > 0 ? spend / sales : null,
    roas: spend > 0 ? sales / spend : null,
  };
}

/** The 总计 row: volumes summed, ratios recomputed from the sums. */
export function totalOf(rows: readonly Volume[]): PlacementMetrics {
  return withRatios(sum(rows));
}

/** KPI cards over a whole series; there is no previous period to compare with. */
export function kpisOf(points: readonly Volume[], currency: string | null): PerformanceKpi[] {
  const total = totalOf(points);
  return KPI_DEFS.map((def) => ({
    ...def,
    value: total[def.key as keyof PlacementMetrics] ?? null,
    previousValue: null,
    change: null,
    direction: "flat",
    currency,
  }));
}

/** Monday of the ISO week containing `date` (YYYY-MM-DD). */
function weekStart(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7));
  return day.toISOString().slice(0, 10);
}

/**
 * Daily points rolled into the chart's buckets. A bucket spans only the days
 * the series has, so the first and last weeks / months are clipped to the window.
 */
export function bucketPoints(
  points: ReadonlyArray<Volume & { date: string }>,
  granularity: CampaignAnalyticsGranularity,
): PerformanceTrendPoint[] {
  const keyOf = (date: string) =>
    granularity === "week" ? weekStart(date) : granularity === "month" ? date.slice(0, 7) : date;
  const buckets = new Map<string, Array<Volume & { date: string }>>();
  for (const point of points) {
    const key = keyOf(point.date);
    buckets.set(key, [...(buckets.get(key) ?? []), point]);
  }
  return [...buckets.values()].map((days) => {
    const metrics = totalOf(days);
    const point: PerformanceTrendPoint = {
      periodStart: days[0]?.date ?? "",
      periodEnd: days[days.length - 1]?.date ?? "",
      dataStatus: "complete",
    };
    for (const def of KPI_DEFS) {
      point[def.key as PerformanceMetric] = metrics[def.key as keyof PlacementMetrics] ?? null;
    }
    return point;
  });
}
